import {ReactNode, useState} from 'react';
import {useForm} from 'react-hook-form';
import {FormPhoneField} from '@common/ui/forms/input-field/phone-field/phone-field';
import {FormTextField} from '../../../ui/forms/input-field/text-field/text-field';
import {Button} from '../../../ui/buttons/button';
import {LinkStyle} from '../../../ui/buttons/external-link';
import {Form} from '../../../ui/forms/form';
import {Trans} from '../../../i18n/trans';
import {useNavigate} from '../../../utils/hooks/use-navigate';
import {useBootstrapData} from '../../../core/bootstrap-data/bootstrap-data-context';
import {onFormQueryError} from '../../../errors/on-form-query-error';
import {useHandleLoginSuccess} from '../../requests/use-login';
import {
  PhoneAuthSessionResponse,
  useStartPhoneLogin,
  useVerifyPhoneLogin,
} from '../../requests/phone-otp-auth';
import {OtpCodeField} from './otp-code-field';

interface CredentialsStep {
  phone: string;
  password: string;
}
interface OtpStep {
  code: string;
}

interface Props {
  // pre-filled when the user was sent here from signup ("already registered")
  initialPhone?: string;
  notice?: ReactNode;
}

/**
 * Phone login for the password + OTP mode: phone + password, and - only on the
 * first login for an account - a one-time code.
 */
export function PhoneLoginForm({initialPhone, notice}: Props = {}) {
  const navigate = useNavigate();
  const {data: {environment}} = useBootstrapData();
  const handleSuccess = useHandleLoginSuccess();

  const [creds, setCreds] = useState<CredentialsStep | null>(null);

  const credentialsForm = useForm<CredentialsStep>({
    defaultValues: {phone: initialPhone},
  });
  const otpForm = useForm<OtpStep>();
  const start = useStartPhoneLogin();
  const verify = useVerifyPhoneLogin();

  if (!creds) {
    return (
      <Form
        form={credentialsForm}
        onSubmit={values => {
          start.mutate(values, {
            onSuccess: response => {
              if (response.bootstrapData) {
                handleSuccess(response as unknown as PhoneAuthSessionResponse);
              } else if (response.otp_required) {
                setCreds(values);
              }
            },
            onError: (r: any) => {
              if (r?.response?.data?.must_reset_password) {
                // carry the number over so they go straight to the code step
                navigate('/forgot-password', {
                  state: {phone: values.phone, mustReset: true},
                });
                return;
              }
              onFormQueryError(r, credentialsForm);
            },
          });
        }}
      >
        {notice ? <div className="mb-24 text-sm text-danger">{notice}</div> : null}
        <FormPhoneField
          className="mb-20"
          name="phone"
          type="tel"
          label={<Trans message="Phone" />}
          onlyCountries={environment.ONLY_COUNTRIES?.split(',')}
          excludeCountries={environment.EXCLUDED_COUNTRIES?.split(',')}
          initialCountry={environment.ONLY_COUNTRIES?.split(',')[0]}
          required
        />
        <FormTextField
          className="mb-8"
          name="password"
          type="password"
          autoComplete="current-password"
          label={<Trans message="Password" />}
          required
        />
        <div className="mb-32 text-end">
          <button
            type="button"
            className={LinkStyle}
            onClick={() =>
              navigate('/forgot-password', {
                // carry over whatever number they typed, without sending a code
                state: {prefillPhone: credentialsForm.getValues('phone')},
              })
            }
          >
            <Trans message="Forgot password?" />
          </button>
        </div>
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={start.isPending}
        >
          <Trans message="Continue" />
        </Button>
      </Form>
    );
  }

  return (
    <Form
      form={otpForm}
      onSubmit={({code}) => {
        verify.mutate(
          {phone: creds.phone, password: creds.password, code},
          {
            onSuccess: response => handleSuccess(response),
            onError: r => onFormQueryError(r, otpForm),
          },
        );
      }}
    >
      <div className="mb-32 text-sm">
        <Trans message="Enter the verification code we sent to your phone." />
      </div>
      <OtpCodeField className="mb-32" autoFocus />
      <Button
        className="block w-full"
        type="submit"
        variant="flat"
        color="primary"
        size="md"
        disabled={verify.isPending}
      >
        <Trans message="Sign in" />
      </Button>
    </Form>
  );
}
